import { SchemaDatapointEnumOptions } from '@rossum/api-client/schemas';
import { MenuItem, Stack, TextField, Typography } from '@rossum/ui/material';
import {
  GridRenderCellParams,
  useGridApiContext,
} from '@rossum/ui/x-data-grid-pro';
import { useIntl } from 'react-intl';
import { DatapointColumn, RowModel } from '../rows/helpers';
import { CellIcons } from './CellIcons';
import { CellValueSkeleton } from './CellValueSkeleton';
import {
  resolveAutomationBlockersPerDatapoints,
  resolveMessagesPerDatapoints,
} from './utils';

export const formulaPreviewClassName = 'current-formula-preview-cell';

const DatapointCell = ({
  value,
}: {
  value: NonNullable<RowModel[DatapointColumn]>;
}) => {
  const intl = useIntl();
  const [first, ...rest] = value.datapoints;
  const restLength = rest.length;

  const firstValue =
    first && 'content' in first && first.content
      ? first.content.value
      : undefined;

  const resolvedMessages = resolveMessagesPerDatapoints(value);

  const resolvedAutomationBlockers =
    resolveAutomationBlockersPerDatapoints(value);

  return (
    <Stack
      width={1}
      component="span"
      direction="row"
      sx={{
        [`.${formulaPreviewClassName} &`]: {
          color: 'success.main',
        },
      }}
    >
      <Stack component="span" direction="row" spacing={0.5}>
        <CellIcons
          type={null}
          messages={resolvedMessages}
          automationBlockers={resolvedAutomationBlockers}
        />
        {restLength ? (
          <Typography variant="body2" component="span" color="text.secondary">
            {intl.formatMessage(
              {
                id: 'features.queueSettings.fields.formulaPreview.table.moreLines',
              },
              { count: restLength }
            )}
          </Typography>
        ) : (
          <Typography variant="body2" component="span" whiteSpace="preserve">
            {value.preview || firstValue || ' '}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export const DatapointCellRenderer = ({
  params,
  options,
}: {
  params: GridRenderCellParams<RowModel, RowModel[DatapointColumn]>;
  options?: SchemaDatapointEnumOptions | undefined;
}) => {
  const api = useGridApiContext();

  return params.value === undefined || params.row.isFetching ? (
    <CellValueSkeleton />
  ) : params.isEditable === true ? (
    params.colDef.type === 'singleSelect' ? (
      <TextField
        fullWidth
        slotProps={{
          htmlInput: {
            sx: {
              fontSize: theme => theme.typography.body2.fontSize,
            },
          },
        }}
        select
        onFocus={() => {
          api.current.startRowEditMode({
            id: params.id,
          });
        }}
        size="small"
        value={params.value.preview}
      >
        {options?.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    ) : (
      <TextField
        fullWidth
        slotProps={{
          htmlInput: {
            sx: {
              fontSize: theme => theme.typography.body2.fontSize,
            },
          },
        }}
        onFocus={() => {
          api.current.startRowEditMode({
            id: params.id,
          });
        }}
        size="small"
        value={params.value.preview}
      />
    )
  ) : (
    <DatapointCell value={params.value} />
  );
};
